import {useMutation, useQueryClient} from '@tanstack/react-query'
import {t} from 'i18next'
import {useSnackbar} from 'notistack'

import {api} from '../../../../../App.store'
import {SlotManagementConfig} from '../types/SlotManagementConfig'

import {SlotManagementConfigQueryKey, ProductConfigsQueryKey} from './queryKeys'

// Error extraction and categorization logic
const extractAndCategorizeError = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        status?: number
        data?: {detail?: string; message?: string; title?: string}
      }
    }
    const status = axiosError.response?.status
    const data = axiosError.response?.data

    switch (status) {
      case 409:
        // Conflict - use localized message
        return t('configurations.hconnect.slotManagement.configurationDetails.errors.conflict')

      case 400: {
        // Bad request - use server details if available
        const detail = data?.detail || data?.message || data?.title
        return (
          detail ||
          t('configurations.hconnect.slotManagement.configurationDetails.errors.badRequest')
        )
      }

      case 500: {
        // Server error - use server details if available
        const serverDetail = data?.detail || data?.message || data?.title
        return (
          serverDetail ||
          t('configurations.hconnect.slotManagement.configurationDetails.errors.serverError')
        )
      }

      default:
        // Unknown HTTP error
        return t('configurations.hconnect.slotManagement.configurationDetails.errors.unknown')
    }
  }

  // Network or other non-HTTP errors
  return t('configurations.hconnect.slotManagement.configurationDetails.errors.network')
}

const editSlotManagementConfig = async (slotManagementConfig: SlotManagementConfig) => {
  await api.put<SlotManagementConfig>(
    `/product-configs/slot-management-settings/${slotManagementConfig.id}`,
    slotManagementConfig
  )
  return slotManagementConfig
}

const createSlotManagementConfig = async (
  slotManagementConfig: Omit<SlotManagementConfig, 'id'>
) => {
  const response = await api.post<SlotManagementConfig>(
    '/product-configs/slot-management-settings',
    slotManagementConfig
  )
  return response.data
}

const deleteSlotManagementConfig = async (configId: string) => {
  await api.delete<SlotManagementConfig[]>(`/product-configs/slot-management-settings/${configId}`)
  return configId
}

type MutationType = 'edit' | 'create' | 'delete' | 'copy'

export const useSlotManagementConfigsEditMutation = () => {
  const queryClient = useQueryClient()
  const {enqueueSnackbar} = useSnackbar()

  return useMutation(
    editSlotManagementConfig,
    getMutationHandlers(queryClient, enqueueSnackbar, 'edit')
  )
}

export const useSlotManagementConfigsCreateMutation = () => {
  const queryClient = useQueryClient()
  const {enqueueSnackbar} = useSnackbar()

  return useMutation(
    createSlotManagementConfig,
    getMutationHandlers(queryClient, enqueueSnackbar, 'create')
  )
}

export const useSlotManagementConfigsDeleteMutation = () => {
  const queryClient = useQueryClient()
  const {enqueueSnackbar} = useSnackbar()

  return useMutation(
    deleteSlotManagementConfig,
    getMutationHandlers(queryClient, enqueueSnackbar, 'delete')
  )
}

export const useSlotManagementConfigsCopyMutation = () => {
  const queryClient = useQueryClient()
  const {enqueueSnackbar} = useSnackbar()

  return useMutation(
    createSlotManagementConfig, // Copy uses the same API as create
    getMutationHandlers(queryClient, enqueueSnackbar, 'copy')
  )
}

const getMutationHandlers = (queryClient, enqueueSnackbar, mutationType: MutationType) => ({
  onSuccess: () => {
    const successMessageKey = {
      edit: 'configurations.hconnect.slotManagement.configurationList.editSuccess',
      create: 'configurations.hconnect.slotManagement.configurationList.createSuccess',
      copy: 'configurations.hconnect.slotManagement.configurationList.createSuccess',
      delete: 'configurations.hconnect.slotManagement.configurationList.deleteSuccess'
    }[mutationType]

    enqueueSnackbar(t(successMessageKey), {variant: 'success'})
    queryClient.invalidateQueries([ProductConfigsQueryKey, SlotManagementConfigQueryKey])
  },
  onError: (error: unknown) => {
    // Extract and show categorized error message in toast
    const errorMessage = extractAndCategorizeError(error)
    enqueueSnackbar(errorMessage, {variant: 'error'})

    console.error('Mutation error:', error)
  }
})
